// @ts-strict-ignore
import { Icon, IconName } from '@usealloy/component-library';

import type { Action, WorkflowType } from 'flow/store/engine/types';
import { ActionType } from 'flow/store/engine/constants';
import { DEFAULT_TAG_COLOR } from 'flow/editor/components/constants';
import { formatColor } from 'flow/editor/components/helpers';
import {
  getActionName,
  getOutcomeBaseColor,
  isDefaultPrecedenceIndexedAction,
} from 'flow/service/actions';
import { LIST_SERVICE_KEY_IN_SERVICES } from 'flow/service/services/constants';
import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';
import type { WorkflowState } from 'flow/store/redux/state';
import { createWorkflowHistoryThunk, swapActionObjectThunk } from 'flow/store/redux/thunks';
import { changeDescription } from 'flow/store/redux/types';
import type { AppDispatch } from '~/store';
import {
  getOutcomeIconName,
} from '~/dashboard/views/workflows/editor/components/Island/OutcomeIsland/OnIslandOutcomeNode/helpers';
import {
  OutcomeIcon,
} from '~/dashboard/views/workflows/editor/components/Island/IslandActionRuleEditor/OutcomeDropdown/OutcomeIcon';

import type { ActionTypeOption, ActionValueOption } from './types';
import { CREATABLE_WORKFLOW_ELEMENTS, defaultActionTypeOptions } from './constants';
import { TAG_NAME_MAX_LENGTH } from 'flow/service/actions/tags/constants';
import { OUTCOME_NAME_MAX_LENGTH } from 'flow/service/actions/outcomes/constants';

function buildActionTypeOptions(
  optionMap: Record<string, Record<string, unknown>>,
): ActionTypeOption[] {
  return Object.entries(optionMap).map(([label, data]) => ({
    label,
    value: label,
    ...data,
  }));
}

export function getSnippetActionTypeOptions(): ActionTypeOption[] {
  return buildActionTypeOptions(defaultActionTypeOptions);
}

export function getActionTypeOptions(options: {
  outcomeActions: Action[];
  workflowRoutingActions: Action[];
  workflowType: WorkflowType;
}): ActionTypeOption[] {
  const { outcomeActions, workflowRoutingActions, workflowType } = options;
  // todo - type this better
  const labelToOptionDataMap = {
    ...defaultActionTypeOptions,
    ...(workflowType !== 'router' && ({
      'decision to an outcome': {
        meta: {
          copy: {
            disabled: 'Add a default outcome from the "Add" menu before building out other outcome logic.',
          },
          singleValueLabel: 'decision to',
          type: ActionType.OUTCOME,
        },
        // We don't want to enable this option if there are no Outcomes or if there
        // isn't a single Action without rules (e.g. a Default Outcome)
        isDisabled: !outcomeActions.some(isDefaultPrecedenceIndexedAction),
      },
    })),
    ...(workflowType === 'router' && ({
      'run a workflow': {
        meta: {
          copy: {
            disabled: 'Add a default workflow from the "Add" menu before building out other workflow routing logic.',
          },
          singleValueLabel: 'run workflow',
          type: ActionType.WORKFLOW,
        },
        isDisabled: !workflowRoutingActions.some(isDefaultPrecedenceIndexedAction),
      },
    })),
    // todo - add support for this later: 'exit workflow'
  };

  return buildActionTypeOptions(labelToOptionDataMap);
}

// FIXME: BUILD-654 - centralize these helpers
export function getTagOptions(tags: WorkflowState['workflowProps']['tags']): ActionValueOption[] {
  return tags
    .filter(tag => !tag.archived)
    .map(tag => {
      const {
        tag_color: color = DEFAULT_TAG_COLOR,
        tag_name: name,
      } = tag;

      return {
        label: name,
        value: name,
        meta: {
          underlineColor: formatColor(color),
        },
      };
    })
    .sort(sortOptionsASC);
}

// FIXME: BUILD-654 - centralize these helpers
export function getServiceOptions(services: WorkflowState['workflowProps']['services']): ActionValueOption[] {
  return Object.values(services)
    .filter(service => service.enabled || service.internal_name === LIST_SERVICE_KEY_IN_SERVICES)
    .map(service => ({
      label: service.name,
      value: service.internal_name,
      meta: {
        textColor: 'var(--cl-green-500)',
      },
    }))
    .sort(sortOptionsASC);
}

// FIXME: BUILD-654 - centralize these helpers
export function getOutcomeOptions({
  outcomes,
  outcomeActions,
  isAddingNewOutcome = false,
}: {
  outcomes: WorkflowState['workflowProps']['outcomes'];
  outcomeActions: Action[];
  isAddingNewOutcome: boolean;
}): ActionValueOption[] {
  const outcomeActionsByName: Record<string, Action> = outcomeActions.reduce(
    (acc, outcomeAction) => ({
      ...acc,
      [getActionName(outcomeAction)]: outcomeAction,
    }),
    {},
  );

  return outcomes
    .filter(outcome => !outcome.archived)
    .map(outcome => {
      const { outcome: outcomeName } = outcome;
      const colorToUse = outcome.outcome_color
        ? outcome.outcome_color
        : getOutcomeBaseColor(outcomeName);
      const icon = outcome.icon_name
        ? outcome.icon_name
        : getOutcomeIconName(outcomeName);
      const color = formatColor(colorToUse);

      return {
        icon: (
          <OutcomeIcon
            color={color}
            icon={icon}
          />
        ),
        isDisabled: (
          // When we are adding a new outcome from outcome island we don't want to
          // allow the user to select an outcome that has already been selected
          isAddingNewOutcome
            ? outcomeActionsByName[outcomeName]
            : isDefaultPrecedenceIndexedAction(outcomeActionsByName[outcomeName])
        ),
        label: outcomeName,
        value: outcomeName,
      };
    }).sort(sortOptionsASC);
}

// FIXME: BUILD-654 - centralize these helpers
export function getWorkflowRoutingOptions(
  workflows: WorkflowState['workflowProps']['allWorkflows'],
  workflowRoutingActions: Action[],
  isAddingNewWorkflowRouting = false,
): ActionValueOption[] {
  const workflowRoutingActionsById: Record<string, Action> = workflowRoutingActions.reduce(
    (acc, workflowRoutingAction) => ({
      ...acc,
      [workflowRoutingAction.workflow.workflowId]: workflowRoutingAction,
    }),
    {},
  );

  return workflows
    .filter(workflow => !workflow.archived)
    .map(workflow => {
      const { name: workflowName, application_id: workflowId } = workflow;
      return {
        icon: <Icon icon={IconName.Workflows} />,
        isDisabled: (
          // When we are adding a new workflow from the island we don't want to
          // allow the user to select a workflow that has already been selected
          isAddingNewWorkflowRouting
            ? workflowRoutingActionsById[workflowId]
            : isDefaultPrecedenceIndexedAction(workflowRoutingActionsById[workflowId])
        ),
        label: workflowName,
        value: String(workflowId),
      };
    }).sort(sortOptionsASC);
}

export function getActionValueDropdownPlaceholder(actionType: ActionType): string {
  const actionTypeValue = {
    [ActionType.SERVICE]: 'service',
    [ActionType.TAG]: 'tag',
    [ActionType.OUTCOME]: 'outcome',
    [ActionType.WORKFLOW]: 'workflow',
  }[actionType];

  return `type to search ${actionTypeValue ?? ''}`;
}

export function isActionTypeCreatable(type: ActionType): boolean {
  return CREATABLE_WORKFLOW_ELEMENTS.includes(type);
}

function sortOptionsASC(a: ActionValueOption, b: ActionValueOption) {
  return a.label.localeCompare(b.label);
}

export function validateCreateOption(
  selectedActionType: ActionType,
  option: ActionValueOption,
): string | null {
  const elementName = option.label;
  switch (selectedActionType) {
    case ActionType.TAG: {
      if (elementName.length > TAG_NAME_MAX_LENGTH) {
        return `Tags cannot exceed ${TAG_NAME_MAX_LENGTH} characters`;
      }
      break;
    }
    case ActionType.OUTCOME: {
      if (elementName.length > OUTCOME_NAME_MAX_LENGTH) {
        return `Outcomes cannot exceed ${OUTCOME_NAME_MAX_LENGTH} characters`;
      }
      break;
    }
  }
  return null;
}

export function swapTagAction({
  dispatch,
  isWorkflowHistoryOn,
  publicId,
  replacementName,
}: {
  dispatch: AppDispatch;
  isWorkflowHistoryOn: boolean;
  publicId: Action['publicId'];
  replacementName: string;
}) {
  dispatch(createWorkflowHistoryThunk(
    () => dispatch(swapActionObjectThunk(replacementName)),
    isWorkflowHistoryOn,
    {
      operation: Operation.Modify,
      uiEffectContext: createUiEffectContext(changeDescription.action, { publicId }),
    },
  ));
}
